// The values are the Rails enum keys the API expects (User::CUSTOMER_TYPES); the
// labels are what an admin sees. Listed commonest-first for the form, which is not
// the order the enum stores them in — that one is chosen so the list column sorts
// alphabetically.
export interface CustomerTypeOption {
  value: string;
  label: string;
}

export const CUSTOMER_TYPE_OPTIONS: CustomerTypeOption[] = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "other", label: "Other" }
];

// What a customer created by hand gets. Matches the implicit behaviour before the
// field existed; a customer who predates it stays blank rather than being guessed.
export const DEFAULT_CUSTOMER_TYPE = "residential";

// The whole Company Name / Customer Type epic ships one list at a time, so a single
// flag hides every surface until the last one lands. This is the only place the flag
// is read on the front end — everything else asks these two helpers.
export function customerClassificationEnabled(): boolean {
  return !!window.currentTenant?.feature_flags
    ?.customer_classification_ff_enabled;
}

export interface CustomerColumn {
  data: string;
  title?: string;
  searchable?: boolean;
}

// Spread into any customer-bearing datatable's column list, right before the name
// column, so the company leads. Empty when the flag is off, so a list sorted by a
// column position past this spread must add its length to that position.
export function customerClassificationColumns(): CustomerColumn[] {
  if (!customerClassificationEnabled()) return [];

  return [
    { data: "company_name", title: "Company Name" },
    { data: "customer_type", title: "Customer Type", searchable: false }
  ];
}

// For a list that shows a customer but offers no customer filtering: the company
// alone, with no search box and no Customer Type column.
export function companyNameColumns(): CustomerColumn[] {
  if (!customerClassificationEnabled()) return [];

  return [{ data: "company_name", title: "Company Name", searchable: false }];
}

// A residential customer is named by the person (the company only if they have no
// name), anyone else by "Company (Contact)". An unset type takes the second branch
// because it is inferred from whether a company was recorded. Mirrors
// User#header_name; with the flag off this is the person's name alone.
export function customerHeaderName(
  personName?: string | null,
  companyName?: string | null,
  customerType?: string | null
): string {
  const person = (personName ?? "").trim();
  if (!customerClassificationEnabled()) return person;

  const company = (companyName ?? "").trim();
  if (customerType === "residential") return person || company;
  if (!company) return person;

  return person ? `${company} (${person})` : company;
}

// The same identity as customerHeaderName, split into lines for stacked address-block
// contexts (Bill To, letterhead PDFs). Returns zero, one, or two strings; templates
// render each on its own line, company first and contact second so the Bill To
// matches the company-led identity used everywhere else (inline headers, scheduler,
// reports). Mirrors User#header_name_block.
export function customerHeaderNameBlock(
  personName?: string | null,
  companyName?: string | null,
  customerType?: string | null
): string[] {
  const person = (personName ?? "").trim();
  if (!customerClassificationEnabled()) {
    return person ? [person] : [];
  }

  const company = (companyName ?? "").trim();
  if (customerType === "residential") {
    const line = person || company;
    return line ? [line] : [];
  }
  if (!company) {
    return person ? [person] : [];
  }

  return person ? [company, person] : [company];
}
